/**
 * ButtonGroup — Toggle group for mutually exclusive options
 *
 * Used for: language switchers, view mode toggles, filter tabs
 *
 */
import { styled, XStack, Text as TamaguiText, type GetProps } from 'tamagui'

const ButtonGroupFrame = styled(XStack, {
  name: 'ButtonGroup',
  bg: '$backgroundCard',
  br: '$xl',
  p: '$xs',
  gap: '$xs',
  bw: 1,
  bc: '$borderColor',
})

type ButtonGroupFrameProps = GetProps<typeof ButtonGroupFrame>

interface ButtonGroupOption {
  value: string
  label: string
}

interface ButtonGroupProps extends ButtonGroupFrameProps {
  options: ButtonGroupOption[]
  value: string
  onValueChange: (value: string) => void
}

export const ButtonGroup = ButtonGroupFrame.styleable<ButtonGroupProps>(
  ({ options, value, onValueChange, ...props }, ref) => {
    return (
      <ButtonGroupFrame ref={ref} {...props}>
        {options.map((option) => {
          const isActive = option.value === value
          return (
            <XStack
              key={option.value}
              tag="button"
              ai="center"
              jc="center"
              br="$xl"
              px="$md"
              py={6}
              cursor="pointer"
              bg={isActive ? '$colorPrimary' : 'transparent'}
              hoverStyle={{ bg: isActive ? '$colorPrimary' : '$backgroundSecondary', opacity: 0.85 }}
              pressStyle={{ opacity: 0.7 }}
              onPress={() => onValueChange(option.value)}
            >
              <TamaguiText
                fontFamily="$body"
                fontSize={13}
                fontWeight="600"
                color={isActive ? '$colorInverse' : '$color'}
              >
                {option.label}
              </TamaguiText>
            </XStack>
          )
        })}
      </ButtonGroupFrame>
    )
  },
)
